import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdPrint, MdEmail } from 'react-icons/md';
import '../master/MasterData.css';

interface Customer {
  customerId: number;
  customerCode: string;
  customerName: string;
  customerType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface InvoiceItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  vatAmount: number;
}

interface SalesInvoice {
  invoiceId: number;
  invoiceNo: string;
  invoiceDate: string;
  deliveryNo: string;
  customerCode: string;
  customerName: string;
  customerTIN: string;
  dueDate: string;
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  status: 'DRAFT' | 'POSTED' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  items: InvoiceItem[];
  terms: string;
  remarks: string;
}

const SalesInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<SalesInvoice | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch customers from API
    setCustomers([
      {
        customerId: 1,
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerType: 'CORPORATE',
        tin: '123-456-789-000',
        email: 'sm@furniture.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        customerId: 2,
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerType: 'CORPORATE',
        tin: '987-654-321-000',
        email: 'homedepot@example.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch invoices from API
    setInvoices([
      {
        invoiceId: 1,
        invoiceNo: 'INV-2025-0001',
        invoiceDate: '2025-02-11',
        deliveryNo: 'DR-2025-0001',
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerTIN: '123-456-789-000',
        dueDate: '2025-03-11',
        subtotal: 401785.71,
        vatAmount: 48214.29,
        totalAmount: 450000,
        status: 'SENT',
        items: [],
        terms: 'Net 30',
        remarks: ''
      },
      {
        invoiceId: 2,
        invoiceNo: 'INV-2025-0002',
        invoiceDate: '2025-02-15',
        deliveryNo: 'DR-2025-0002',
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerTIN: '987-654-321-000',
        dueDate: '2025-03-15',
        subtotal: 758928.57,
        vatAmount: 91071.43,
        totalAmount: 850000,
        status: 'PAID',
        items: [],
        terms: 'Net 30',
        remarks: ''
      }
    ]);
  }, []);

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingInvoice(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (invoice: SalesInvoice) => {
    setEditingInvoice(invoice);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (invoice: SalesInvoice) => {
    setEditingInvoice(invoice);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (invoiceId: number) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      setInvoices(invoices.filter(i => i.invoiceId !== invoiceId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PAID': return 'badge-success';
      case 'SENT': return 'badge-info';
      case 'POSTED': return 'badge-primary';
      case 'OVERDUE': return 'badge-danger';
      case 'CANCELLED': return 'badge-secondary';
      default: return 'badge-warning';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Sales Invoices</h2>
            <p className="subtitle">Manage sales tax invoices and billing</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create New Invoice
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Invoice Date</th>
                  <th>Customer</th>
                  <th>Due Date</th>
                  <th>Subtotal</th>
                  <th>VAT (12%)</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.invoiceId}>
                    <td className="code">{invoice.invoiceNo}</td>
                    <td>{new Date(invoice.invoiceDate).toLocaleDateString('en-US')}</td>
                    <td>{invoice.customerName}</td>
                    <td>{new Date(invoice.dueDate).toLocaleDateString('en-US')}</td>
                    <td className="amount">₱{invoice.subtotal.toLocaleString()}</td>
                    <td className="amount">₱{invoice.vatAmount.toLocaleString()}</td>
                    <td className="amount">₱{invoice.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(invoice)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(invoice)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-print"
                          onClick={() => window.print()}
                          title="Print"
                        >
                          <MdPrint />
                        </button>
                        <button
                          className="btn-icon btn-email"
                          onClick={() => alert('Email sent!')}
                          title="Send Email"
                        >
                          <MdEmail />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(invoice.invoiceId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredInvoices.length === 0 && (
              <div className="no-data">
                <p>No invoices found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <InvoiceModal
          invoice={editingInvoice}
          viewMode={viewMode}
          customers={customers}
          onClose={() => setShowModal(false)}
          onSave={(invoice) => {
            if (editingInvoice) {
              setInvoices(invoices.map(i =>
                i.invoiceId === invoice.invoiceId ? invoice : i
              ));
            } else {
              setInvoices([...invoices, { ...invoice, invoiceId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface InvoiceModalProps {
  invoice: SalesInvoice | null;
  viewMode: boolean;
  customers: Customer[];
  onClose: () => void;
  onSave: (invoice: SalesInvoice) => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ invoice, viewMode, customers, onClose, onSave }) => {
  const VAT_RATE = 0.12;

  const [formData, setFormData] = useState<SalesInvoice>(
    invoice || {
      invoiceId: 0,
      invoiceNo: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      deliveryNo: '',
      customerCode: '',
      customerName: '',
      customerTIN: '',
      dueDate: '',
      subtotal: 0,
      vatAmount: 0,
      totalAmount: 0,
      status: 'DRAFT',
      items: [],
      terms: 'Net 30',
      remarks: ''
    }
  );

  const handleCustomerChange = (customerCode: string) => {
    const selectedCustomer = customers.find(c => c.customerCode === customerCode);
    if (selectedCustomer) {
      setFormData({
        ...formData,
        customerCode: selectedCustomer.customerCode,
        customerName: selectedCustomer.customerName,
        customerTIN: selectedCustomer.tin
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: Date.now(),
          itemCode: '',
          itemName: '',
          quantity: 1,
          unitPrice: 0,
          amount: 0,
          vatAmount: 0
        }
      ]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof InvoiceItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => {
        if (item.itemId === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.quantity * updatedItem.unitPrice;
            updatedItem.vatAmount = updatedItem.amount * VAT_RATE;
          }
          return updatedItem;
        }
        return item;
      })
    });
  };

  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.amount, 0);
    const vatAmount = subtotal * VAT_RATE;
    const totalAmount = subtotal + vatAmount;
    setFormData(prev => ({ ...prev, subtotal, vatAmount, totalAmount }));
  }, [formData.items]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Invoice' : invoice ? 'Edit Invoice' : 'Create New Invoice'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Invoice No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.invoiceNo}
                  onChange={(e) => setFormData({ ...formData, invoiceNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Invoice Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.invoiceDate}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Customer Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.customerCode}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">Select Customer</option>
                  {customers.filter(c => c.isActive === 'Y').map(customer => (
                    <option key={customer.customerId} value={customer.customerCode}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Customer TIN</label>
                <input
                  type="text"
                  disabled
                  value={formData.customerTIN}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Delivery No</label>
                <input
                  type="text"
                  disabled={viewMode}
                  value={formData.deliveryNo}
                  onChange={(e) => setFormData({ ...formData, deliveryNo: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Payment Terms *</label>
                <select
                  disabled={viewMode}
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                >
                  <option value="Net 7">Net 7 days</option>
                  <option value="Net 15">Net 15 days</option>
                  <option value="Net 30">Net 30 days</option>
                  <option value="Net 45">Net 45 days</option>
                  <option value="Net 60">Net 60 days</option>
                  <option value="COD">Cash on Delivery</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="POSTED">Posted</option>
                  <option value="SENT">Sent</option>
                  <option value="PAID">Paid</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Invoice Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    <th>VAT (12%)</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.itemId, 'quantity', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      <td>₱{item.vatAmount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Subtotal:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.subtotal.toLocaleString()}</td>
                    <td colSpan={viewMode ? 1 : 2}></td>
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>VAT (12%):</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.vatAmount.toLocaleString()}</td>
                    <td colSpan={viewMode ? 1 : 2}></td>
                  </tr>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '16px' }}>Total Amount:</td>
                    <td style={{ fontWeight: 'bold', fontSize: '16px' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    <td colSpan={viewMode ? 1 : 2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {invoice ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesInvoices;
